/**
 * The SINGLE SOURCE OF TRUTH for where a document-media `src` may point.
 *
 * Document images are client-side-encrypted blobs in public-read object
 * storage (see lexicalNodes/DocImageNode.js); the only thing that makes a
 * `src` acceptable is that it points INTO OUR bucket/CDN — anything else is an
 * exfiltration vector (the webview would fetch attacker hosts with the
 * reader's IP) or a tracking pixel.
 *
 * KEEP IN SYNC — drift between these three fails src/tests/mediaCspGuard.test.js
 * for the first two and breaks image loads in prod for the third:
 *  1. this list,
 *  2. `img-src`/`connect-src` in src-tauri/tauri.conf.json (`app.security.csp`)
 *     — a host listed here but not there renders/fetches nothing in the
 *     packaged desktop app,
 *  3. the backend's `STORAGE_PUBLIC_URL` env var — the base under which the
 *     upload endpoint mints public URLs. If prod changes bucket/CDN, the new
 *     base must be ADDED here (never swap: old documents keep their old URLs).
 *
 * Both DigitalOcean Spaces URL styles are listed (virtual-host
 * `apiboo.fra1...` and path-style `fra1.../apiboo/`) plus both CDN hosts.
 * The trailing slash on every prefix is load-bearing: it is what makes
 * `https://apiboo.fra1.digitaloceanspaces.com.evil.com/` fail the match.
 */
export const ALLOWED_MEDIA_URL_PREFIXES = Object.freeze([
  'https://apiboo.fra1.digitaloceanspaces.com/',
  'https://fra1.digitaloceanspaces.com/apiboo/',
  'https://cdn.apiboo.com/',
  'https://media.apiboo.com/',
]);

// Whitespace (incl. unicode spaces), control chars, or a literal '..'
// anywhere — rejected outright before the prefix check. The prefix pin is
// what actually holds the host; this just refuses obviously-mangled strings.
// eslint-disable-next-line no-control-regex -- rejecting control chars is the point
const FORBIDDEN_CHARS_RE = /[\s\u0000-\u001f\u007f]/;

/**
 * Exact-prefix allowlist check. Case-sensitive on purpose: these URLs are
 * minted by our own upload path, never typed by hand, so a case variant is
 * not a real URL of ours.
 */
export function isAllowedMediaUrl(url) {
  const s = String(url);
  if (!s || FORBIDDEN_CHARS_RE.test(s) || s.includes('..')) return false;
  return ALLOWED_MEDIA_URL_PREFIXES.some((prefix) => s.startsWith(prefix));
}
